import { TLocale, loadLocalData, saveLocalData } from '@northladder/utilities';

import {
  TSetAppConfigState,
  TPersistableAppConfigState,
  DEFAULT_APP_CONFIG_STATE,
  TThemeColorScheme,
} from './types';

/**
 * This key is used to persist the app config state to local storage.
 * Can be named anything to prevent the user from snooping around the app.
 */
const STORAGE_KEY_APP_CONFIG_STATE = 'app-config-v1';

/**
 * This retrieves and decrypts the app config form secure storage
 */
export const loadPersistedAppConfig = () =>
  loadLocalData<TPersistableAppConfigState>(STORAGE_KEY_APP_CONFIG_STATE);

/**
 * This persists the app config to secure local storage.
 */
export const persistAppConfig = (targetState: TPersistableAppConfigState) => {
  saveLocalData(STORAGE_KEY_APP_CONFIG_STATE, targetState);
};

/**
 * This updates the value  for `hasViewedCookieForm` both in global state and
 * the browser local secure storage to track whether the user has viewed the
 * cookie form or not.
 */
export function updateHasViewedCookieForm(set: TSetAppConfigState) {
  return function setHasViewedCookieForm(hasViewedCookieForm: boolean) {
    set((prevState) => {
      persistAppConfig({ ...prevState, hasViewedCookieForm });

      return { hasViewedCookieForm };
    });
  };
}

/**
 * This updates the value  for `hasWatchedOnboarding` both in global state and
 * the browser local secure storage.
 */
export function updateHasWatchedOnboarding(set: TSetAppConfigState) {
  return function setHasWatchedOnboarding(hasWatchedOnboarding: boolean) {
    set((prevState) => {
      persistAppConfig({ ...prevState, hasWatchedOnboarding });

      return { hasWatchedOnboarding };
    });
  };
}

/**
 * This updates the value  for `isSidebarExpanded` both in global state and
 * the browser local secure storage.
 */
export function updateIsSidebarExpanded(set: TSetAppConfigState) {
  return function setIsSidebarExpanded(isSidebarExpanded: boolean) {
    set((prevState) => {
      persistAppConfig({ ...prevState, isSidebarExpanded });

      return { isSidebarExpanded };
    });
  };
}

/**
 * This updates the value  for `appTheme` both in global state and
 * the browser local secure storage.
 */
export function updateAppColorScheme(set: TSetAppConfigState) {
  return function setAppColorScheme(appColorScheme: TThemeColorScheme) {
    set((prevState) => {
      persistAppConfig({ ...prevState, appColorScheme });

      return { appColorScheme };
    });
  };
}

/**
 * This updates the value  for `locale` both in global state and
 * the browser local secure storage.
 */
export function updateLocale(set: TSetAppConfigState) {
  return function setLocale(locale: TLocale) {
    set((prevState) => {
      persistAppConfig({ ...prevState, locale });

      return { locale };
    });
  };
}

/**
 * This updates the value  for `locale` both in global state and
 * the browser local secure storage.
 */
export function resetAppConfigCallback(set: TSetAppConfigState) {
  return function resetAppConfig() {
    set(() => {
      persistAppConfig(DEFAULT_APP_CONFIG_STATE);

      return DEFAULT_APP_CONFIG_STATE;
    });
  };
}
